import { ReactNode } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { cn } from '@/lib/utils.js';

interface SectionProps {
  title: string;
  icon?: ReactNode;
  count?: number;
  collapsed: boolean;
  onToggle: () => void;
  onNew?: () => void;
  children: ReactNode;
  className?: string;
}

export function Section({
  title,
  icon,
  count,
  collapsed,
  onToggle,
  onNew,
  children,
  className,
}: SectionProps) {
  return (
    <div className={cn('select-none', className)}>
      {/* Section Header */}
      <div className="group flex items-center justify-between px-2 py-1.5 hover:bg-muted/50 rounded-md cursor-pointer">
        <div
          className="flex items-center gap-1.5 flex-1 min-w-0"
          onClick={onToggle}
        >
          <button className="p-0.5 hover:bg-muted rounded">
            {collapsed ? (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">
            {title}
          </span>
          {count !== undefined && count > 0 && (
            <span className="ml-auto flex-shrink-0 text-xs bg-primary/10 text-primary rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
              {count}
            </span>
          )}
        </div>
        {onNew && !collapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              onNew();
            }}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Section Content */}
      {!collapsed && <div className="mt-0.5 space-y-0.5">{children}</div>}
    </div>
  );
}
